import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_LIST, STATUS_LIST, CATEGORIES, STATUSES, TEMPERATURE_LIST, TEMPERATURES, formatCurrencyInput, parseCurrencyInput, fmtMoney } from "@/lib/leadMeta";
import { INDUSTRIES } from "@/lib/industries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPhone } from "@/lib/phone";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { US_STATES, CITIES_BY_STATE } from "@/lib/usGeo";
import { InsuranceTypePicker } from "@/components/InsuranceTypePicker";
import { Plus, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", company: "", email: "", phone: "", address: "", address2: "", city: "", state: "", zip: "",
  lat: null, lng: null, insurance_types: ["life"], primary_type: "life", status: "new", premium_estimate: "", commission_pct: "",
  industry: "", employees: "", temperature: "", notes: "",
};

const ignorePac = (e) => {
  const t = e.detail?.originalEvent?.target || e.target;
  if (t?.closest?.(".pac-container")) e.preventDefault();
};

export const AddLeadDialog = ({ onDone, open: controlledOpen, onOpenChange, hideTrigger = false }) => {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolled;
  const setOpen = onOpenChange || setUncontrolled;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [industryOther, setIndustryOther] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Lead name is required");
    if (!form.insurance_types.length) return toast.error("Select at least one insurance type");
    setLoading(true);
    try {
      const payload = { ...form, premium_estimate: form.premium_estimate ? Number(form.premium_estimate) : null, commission_pct: form.commission_pct === "" || form.commission_pct == null ? null : Number(form.commission_pct), employees: form.employees === "" || form.employees == null ? null : Number(form.employees), temperature: form.temperature || null, industry: form.industry === "Other" ? (industryOther.trim() || "Other") : (form.industry || "") };
      await api.post("/leads", payload);
      toast.success("Lead added");
      setForm(EMPTY);
      setIndustryOther("");
      setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button data-testid="add-lead-button"><Plus className="mr-2 h-4 w-4" /> Add Lead</Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" onInteractOutside={ignorePac} onPointerDownOutside={ignorePac}>
        <DialogHeader><DialogTitle className="font-heading">New lead</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Full name</Label><Input data-testid="lead-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="John Smith" /></div>
            <div className="col-span-2"><Label>Company name</Label><Input data-testid="lead-company-input" value={form.company} onChange={set("company")} className="mt-1.5" placeholder="Acme Corp (optional)" /></div>
            <div><Label>Email</Label><Input data-testid="lead-email-input" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))} className="mt-1.5" placeholder="(555) 123-4567" /></div>
            <div className="col-span-2"><Label>Address</Label>
              <AddressAutocomplete data-testid="lead-address-input" value={form.address} onChange={set("address")}
                onSelect={(a) => setForm((f) => ({ ...f, ...a }))} className="mt-1.5" placeholder="Start typing an address…" />
            </div>
            <div className="col-span-2"><Label>Address 2 <span className="text-xs text-muted-foreground">(apt, suite, unit)</span></Label>
              <Input data-testid="lead-address2-input" value={form.address2} onChange={set("address2")} className="mt-1.5" placeholder="Apt / Suite / Unit" />
            </div>
            <div><Label>City</Label>
              <Input data-testid="lead-city-input" list="add-city-list" value={form.city} onChange={set("city")} className="mt-1.5" placeholder="Start typing…" />
              <datalist id="add-city-list">{(CITIES_BY_STATE[form.state] || []).map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State</Label>
                <Select value={form.state || ""} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="lead-state-input"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>ZIP</Label><Input data-testid="lead-zip-input" value={form.zip} onChange={set("zip")} className="mt-1.5" /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Industry</Label>
              <Select value={form.industry || ""} onValueChange={(v) => { setForm((f) => ({ ...f, industry: v })); if (v !== "Other") setIndustryOther(""); }}>
                <SelectTrigger className="mt-1.5" data-testid="lead-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent className="max-h-64">{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
              {form.industry === "Other" && (
                <Input className="mt-2" placeholder="Type the industry" value={industryOther} onChange={(e) => setIndustryOther(e.target.value)} data-testid="lead-industry-other" />
              )}
            </div>
            <div><Label>Number of employees</Label><Input data-testid="lead-employees-input" type="number" min="0" inputMode="numeric" value={form.employees} onChange={set("employees")} className="mt-1.5" placeholder="e.g. 25" /></div>
            <div><Label>Lead temperature</Label>
              <Select value={form.temperature || ""} onValueChange={set("temperature")}>
                <SelectTrigger className="mt-1.5" data-testid="lead-temperature-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{TEMPERATURE_LIST.map((k) => <SelectItem key={k} value={k}>{TEMPERATURES[k].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Insurance type(s)</Label>
            <InsuranceTypePicker value={form.insurance_types} primary={form.primary_type}
              onChange={(v) => setForm((f) => ({ ...f, insurance_types: v, primary_type: v.includes(f.primary_type) ? f.primary_type : (v[0] || "") }))}
              onPrimaryChange={(k) => setForm((f) => ({ ...f, primary_type: k }))} />
            <p className="mt-1.5 text-xs text-muted-foreground">Tap the star to set the main coverage (used on the map pin & reports).</p>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger className="mt-1.5" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_LIST.map((k) => <SelectItem key={k} value={k}>{STATUSES[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Premium estimate ($/yr)</Label><Input data-testid="lead-premium-input" inputMode="numeric" placeholder="$1,200" value={formatCurrencyInput(form.premium_estimate)} onChange={(e) => setForm((f) => ({ ...f, premium_estimate: parseCurrencyInput(e.target.value) }))} className="mt-1.5" /></div>
            <div><Label>Commission %</Label>
              <div className="relative mt-1.5">
                <Input data-testid="lead-commission-pct-input" type="number" min="0" max="100" step="0.1" inputMode="decimal" placeholder="10" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} className="pr-7" />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
              <span className="text-sm font-medium">Projected commission</span>
              <span className="font-mono font-semibold text-primary" data-testid="lead-projected-commission">{fmtMoney((Number(form.premium_estimate) || 0) * (Number(form.commission_pct) || 0) / 100)}</span>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading} data-testid="cancel-lead-button">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="save-lead-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
